import React, { useEffect, useRef } from 'react';
import { t } from 'Src/i18n';
import { useScriptLoader } from 'Src/hooks/use-script-loader/useScriptLoader';
import { FormikInput } from 'Components/frameworks/formik/FormikInput';
import { Capsule } from 'Components/frameworks/capsule/Capsule';
import { Title } from 'Components/frameworks/title/Title';
import { useFormikContext } from 'formik';
import { useAppContext } from 'Context/ReactAppContext';
import config from 'Config/index';
import { useDispatch } from 'react-redux';
import { endLoading, startLoading } from 'Store/global-loader/globalLoader.action';
import { HostedField } from './HostedField';
import { creditCardExpiryValidator } from './paymentProvidersUtils';

export const IxopayCardForm = () => {
  const { country } = useAppContext();
  const dispatch = useDispatch();
  const { scriptLoaded } = useScriptLoader(config.get(`psp.ixopay.${country}.scriptUrl`), {
    'data-main': 'payment-js',
  });

  const paymentJs = useRef(null);
  const [cvvState, setCvvState] = React.useState({ valid: true, length: 0 });
  const [numberState, setNumberState] = React.useState({ valid: true, length: 0 });
  const [isPaymentJsInitialized, setIsPaymentJsInitialized] = React.useState(false);
  const pendingSubmitAfterTokenize = useRef(false);
  const { values, submitForm, isSubmitting, setFieldValue, setErrors } = useFormikContext();

  useEffect(() => {
    if (!scriptLoaded || paymentJs.current) {
      return;
    }

    paymentJs.current = new window.PaymentJs('1.3');
    paymentJs.current.init(
      config.get(`psp.ixopay.${country}.integrationKey`),
      'number',
      'cvv',
      (payment) => {
        setIsPaymentJsInitialized(true);
        const style = {
          border: '0px ',
          width: '100%',
          height: '24px',
          outline: 'none',
          boxShadow: 'none',
          '::placeholder': {
            color: '#9ca3af',
          },
        };
        payment.setNumberStyle(style);
        payment.setCvvStyle(style);
        payment.setNumberPlaceholder(t('common.payment.hipay.placeholderCardNumber'));
        payment.setCvvPlaceholder(t('common.payment.hipay.placeholderCvc'));
        payment.numberOn('input', ({ validNumber, numberLength }) =>
          setNumberState({
            valid: validNumber,
            length: numberLength,
          }),
        );
        payment.cvvOn('input', ({ validCvv, cvvLength }) =>
          setCvvState({ valid: validCvv, length: cvvLength }),
        );
      },
    );
  }, [scriptLoaded]);

  useEffect(() => {
    if (!isSubmitting || values.token) {
      return;
    }

    if (!isPaymentJsInitialized) {
      dispatch(startLoading());
      return;
    }

    dispatch(startLoading());
    pendingSubmitAfterTokenize.current = true;

    const creditCardExpiryDateObject = new Date(values.year);
    const expirationMonth = (creditCardExpiryDateObject.getMonth() + 1).toString().padStart(2, '0');
    const expirationYear = creditCardExpiryDateObject.getFullYear().toString();

    paymentJs.current.tokenize(
      {
        card_holder: values.card_holder,
        month: expirationMonth,
        year: expirationYear,
      },
      (token) => {
        setFieldValue('token', token);
      },
      (errors) => {
        pendingSubmitAfterTokenize.current = false;
        const formikErrors = {};
        for (const error of errors) {
          formikErrors[error.attribute] = error.message;
        }
        if (errors.length !== 0) {
          dispatch(endLoading());
        }
        setErrors({ card_holder: formikErrors.card_holder, year: formikErrors.year });
        setCvvState({ valid: false, length: 0, errorMessage: formikErrors.cvv });
        setNumberState({ valid: false, length: 0, errorMessage: formikErrors.number });
      },
    );
  }, [isSubmitting, isPaymentJsInitialized]);

  useEffect(() => {
    if (!values.token || !pendingSubmitAfterTokenize.current) {
      return;
    }
    pendingSubmitAfterTokenize.current = false;
    submitForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.token]);

  const [monthExpiryPlaceholder, yearExpiryPlaceholder] = t(
    'common.payment.hipay.placeholderExpiryDate',
  )
    .toUpperCase()
    .split('/');

  return (
    <div>
      <Title className="mb-10">{t('common.payment.hipay.cardDetailTitle')}</Title>
      <Capsule className="flex flex-col" header="none" contentClassName="p-20">
        <FormikInput isRequired name="token" type="hidden" />
        <div className="px-10">
          <FormikInput
            label={t('common.payment.hipay.fullNameLabel')}
            name="card_holder"
            id="card_holder"
            placeholder={t('common.payment.hipay.placeholderFullName')}
          />
        </div>
        <HostedField
          type="number"
          name="number"
          id="number"
          className="w-full"
          label={t('common.payment.hipay.cardNumberLabel')}
          hasError={!numberState.valid}
          isValid={numberState.valid && numberState.length > 0}
          errorMessage={numberState.errorMessage || t('common.form.errors.invalidFieldFormat')}
        />
        <div className="flex flex-col md:flex-row">
          <HostedField label={t('common.payment.hipay.expiryDateLabel')} small container={false}>
            <FormikInput
              type="creditCardExpiry"
              name="year"
              dateFormat={t('booking.form.dateFormat')}
              monthPlaceholder={monthExpiryPlaceholder}
              validate={creditCardExpiryValidator}
              yearPlaceholder={yearExpiryPlaceholder}
            />
          </HostedField>
          <HostedField
            type="number"
            name="cvv"
            id="cvv"
            className="w-full"
            label={t('common.payment.hipay.cvcLabel')}
            hasError={!cvvState.valid}
            isValid={cvvState.valid && cvvState.length > 0}
            errorMessage={cvvState.errorMessage || t('common.form.errors.invalidFieldFormat')}
          />
        </div>
      </Capsule>
    </div>
  );
};
